import isEmpty from "lodash/isEmpty";

export const defaultComponentFilter = ["basement", "crawlspace", "slab", "ceiling", "wall", "expFloor", "door", "window", "floorHeader"];
export const defaultDirectionFilter = ["NA", "S", "SE", "E", "NE", "N", "NW", "W", "SW"];
export const defaultInteriorFilter = ["exterior", "interior"];
export const interiorFilterOptions = [
    { value: "exterior", label: "Exterior" },
    { value: "interior", label: "Interior" },
];

export const INTERIOR_FILTER_PATH = "model.modelData.uiSettings.interiorFilter";

export const DIRECTIONAL_COMPONENT_TYPES = ["wall", "window", "door", "floorHeader"];
export const HORIZONTAL_DIRECTION_KEY = "HORIZONTAL";
export const HORIZONTAL_COMPONENT_TYPES = ["basement", "crawlspace", "slab", "ceiling", "expFloor"];
export const COMPASS_DIRECTION_ORDER = ["S", "SE", "E", "NE", "N", "NW", "W", "SW"];
export const DIRECTION_FILTER_LABELS = {
    NA: "N/A",
    HORIZONTAL: "Horizontal",
    S: "South",
    SE: "Southeast",
    E: "East",
    NE: "Northeast",
    N: "North",
    NW: "Northwest",
    W: "West",
    SW: "Southwest",
};

const COMPASS_DIRECTION_KEYS = COMPASS_DIRECTION_ORDER;

export const isHorizontalComponentType = (componentType = "") => HORIZONTAL_COMPONENT_TYPES.includes(componentType);

/** Empty selection ("None") is treated as N/A. */
export const normalizeDirectionFilterValue = (value = []) =>
    !value?.length ? ["NA"] : value;

/** True when the direction filter is the None / N/A-only state. */
export const isDirectionFilterNone = (directionFilter = []) => {
    const normalized = normalizeDirectionFilterValue(directionFilter);
    return normalized.length === 1 && normalized[0] === "NA";
};

export const getDefaultDirectionFilterForOptions = (options = []) => options.map(({ value }) => value);

let previousAvailableDirectionValues = null;
let lastSyncedDirectionFilterKey = null;
let lastSyncedDirectionFilterResult = null;
let trackedModelId = null;

export const resetDirectionFilterAvailabilityTracking = () => {
    previousAvailableDirectionValues = null;
    lastSyncedDirectionFilterKey = null;
    lastSyncedDirectionFilterResult = null;
};

export const setDirectionFilterTrackingModelId = (modelId) => {
    if (modelId == null || trackedModelId === modelId) {
        return;
    }

    trackedModelId = modelId;
    resetDirectionFilterAvailabilityTracking();
};

/**
 * Keeps the direction filter aligned when new orientations appear in the model.
 * Newly present directions are always added automatically so that components
 * added with a new direction remain visible regardless of the current filter.
 */
export const syncDirectionFilterWithAvailable = (saved = [], availableValues = []) => {
    const syncKey = `${availableValues.join(",")}|${(saved || []).join(",")}`;

    if (syncKey === lastSyncedDirectionFilterKey) {
        return lastSyncedDirectionFilterResult;
    }

    const sanitized = sanitizeDirectionFilter(saved, availableValues);
    const previousAvailable = previousAvailableDirectionValues;
    let result = sanitized;

    if (previousAvailable?.length && availableValues?.length) {
        const newDirections = availableValues.filter((value) => !previousAvailable.includes(value));

        // Always add newly-appeared directions to the filter so that
        // components added with a new direction remain visible regardless
        // of whether all directions were previously selected.
        if (newDirections.length) {
            result = [...sanitized];

            newDirections.forEach((value) => {
                if (!result.includes(value)) {
                    result.push(value);
                }
            });
        }
    }

    previousAvailableDirectionValues = availableValues?.length ? [...availableValues] : [];
    lastSyncedDirectionFilterKey = syncKey;
    lastSyncedDirectionFilterResult = result;

    return result;
};

export const getEffectiveDirectionFilter = (savedFilter = [], availableValues = []) => {
    const available = availableValues?.length ? availableValues : defaultDirectionFilter;
    return normalizeDirectionFilterValue(syncDirectionFilterWithAvailable(savedFilter, available));
};

export const isDirectionFilterApplied = (directionFilter = [], availableValues = []) => {
    if (!availableValues?.length) {
        return false;
    }

    const filter = getEffectiveDirectionFilter(directionFilter, availableValues);
    const available = availableValues;

    return filter.length !== available.length || !available.every((value) => filter.includes(value));
};

/** Add a direction to the saved filter when missing so edited components stay visible. */
export const ensureDirectionInFilter = (savedFilter = [], directionKey, availableValues = []) => {
    if (!directionKey || !availableValues?.length) {
        return savedFilter;
    }

    const sanitized = sanitizeDirectionFilter(savedFilter, availableValues);

    if (!availableValues.includes(directionKey) || sanitized.includes(directionKey)) {
        return sanitized;
    }

    return [...sanitized, directionKey];
};

export const sanitizeDirectionFilter = (saved = [], availableValues = []) => {
    if (!availableValues?.length) {
        return [];
    }

    const isLegacyDefault =
        saved?.length === defaultDirectionFilter.length &&
        defaultDirectionFilter.every((value) => saved.includes(value));

    if (!saved?.length || isLegacyDefault) {
        return [...availableValues];
    }

    const sanitized = saved.filter((value) => availableValues.includes(value));

    if (!sanitized.length) {
        return availableValues.includes("NA") ? ["NA"] : [...availableValues];
    }

    return sanitized;
};

/**
 * Normalize a component's own facingDirection.id to a direction filter key.
 * Walls/floor headers: 0 = N/A, 1–8 = compass.
 * Windows/doors: 0–7 = compass (no N/A).
 */
export const normalizeFacingDirectionKey = (componentType, facingId) => {
    if (facingId == null || Number.isNaN(facingId)) return null;

    if (componentType === "wall" || componentType === "floorHeader") {
        if (facingId === 0) return "NA";
        return COMPASS_DIRECTION_KEYS[facingId - 1] || null;
    }

    if (componentType === "window" || componentType === "door") {
        return COMPASS_DIRECTION_KEYS[facingId] || null;
    }

    return null;
};

const collectDirectionKeysFromSubcomponents = (subcomponentsObject = {}, presentKeys = new Set()) => {
    const { window = {}, door = {}, floorHeader = {} } = subcomponentsObject;

    Object.values(window).forEach((component) => {
        if (!component || isEmpty(component)) return;

        const key = normalizeFacingDirectionKey("window", component.facingDirection?.id);
        presentKeys.add(key ?? "NA");
    });

    Object.values(floorHeader).forEach((component) => {
        if (!component || isEmpty(component)) return;

        const key = normalizeFacingDirectionKey("floorHeader", component.facingDirection?.id);
        if (key) presentKeys.add(key);
    });

    Object.values(door).forEach((component) => {
        if (!component || isEmpty(component)) return;

        const key = normalizeFacingDirectionKey("door", component.facingDirection?.id);
        presentKeys.add(key ?? "NA");

        collectDirectionKeysFromSubcomponents(component.subcomponents, presentKeys);
    });
};

const collectDirectionKeysFromComponentType = (componentsByType = {}, componentType = "", presentKeys = new Set()) => {
    Object.values(componentsByType).forEach((component) => {
        if (!component || isEmpty(component)) return;

        if (isHorizontalComponentType(componentType)) {
            presentKeys.add(HORIZONTAL_DIRECTION_KEY);
        }

        if (componentType === "wall") {
            const facingId = component.facingDirection?.id;
            const key = normalizeFacingDirectionKey("wall", facingId);
            if (key) presentKeys.add(key);
        }

        collectDirectionKeysFromSubcomponents(component.subcomponents, presentKeys);
    });
};

export const collectPresentDirectionKeys = (componentsObject = {}) => {
    const presentKeys = new Set();
    const {
        basement = {},
        crawlspace = {},
        slab = {},
        wall = {},
        ceiling = {},
        expFloor = {},
    } = componentsObject;

    collectDirectionKeysFromComponentType(basement, "basement", presentKeys);
    collectDirectionKeysFromComponentType(crawlspace, "crawlspace", presentKeys);
    collectDirectionKeysFromComponentType(slab, "slab", presentKeys);
    collectDirectionKeysFromComponentType(ceiling, "ceiling", presentKeys);
    collectDirectionKeysFromComponentType(expFloor, "expFloor", presentKeys);
    collectDirectionKeysFromComponentType(wall, "wall", presentKeys);

    return presentKeys;
};

/** Direction filter key a component is matched against (horizontal types, else facing direction or N/A). */
export const getComponentDirectionKey = (componentType, componentData = {}) => {
    if (isHorizontalComponentType(componentType)) return HORIZONTAL_DIRECTION_KEY;

    return normalizeFacingDirectionKey(componentType, componentData?.facingDirection?.id) ?? "NA";
};

const collectDirectionKeysByPath = (componentsByType = {}, basePath = "", keysByPath = {}) => {
    Object.entries(componentsByType).forEach(([componentType, components]) => {
        if (!components || typeof components !== "object") return;

        Object.entries(components).forEach(([componentKey, component]) => {
            if (!component || isEmpty(component)) return;

            const path = `${basePath}${componentType}.${componentKey}`;
            keysByPath[path] = getComponentDirectionKey(componentType, component);

            if (component.subcomponents) {
                collectDirectionKeysByPath(component.subcomponents, `${path}.subcomponents.`, keysByPath);
            }
        });
    });

    return keysByPath;
};

/** Map of component path (e.g. "wall.wall1.subcomponents.window.win1") to its direction filter key. */
export const collectComponentDirectionKeysByPath = (componentsObject = {}) =>
    collectDirectionKeysByPath(componentsObject);

export const getAvailableDirectionFilterOptions = (componentsObject = {}) => {
    const presentKeys = collectPresentDirectionKeys(componentsObject);
    const options = [];

    if (presentKeys.has(HORIZONTAL_DIRECTION_KEY)) {
        options.push({ value: HORIZONTAL_DIRECTION_KEY, label: DIRECTION_FILTER_LABELS.HORIZONTAL });
    }

    COMPASS_DIRECTION_ORDER.forEach((key) => {
        if (presentKeys.has(key)) {
            options.push({ value: key, label: DIRECTION_FILTER_LABELS[key] });
        }
    });

    if (presentKeys.has("NA")) {
        options.push({ value: "NA", label: DIRECTION_FILTER_LABELS.NA });
    }

    return options;
};

/**
 * Match components whose stored facingDirection (or horizontal type) is in the filter.
 * Components with undefined/null facingDirection are treated as "N/A".
 */
export const matchesDirectionFilter = (
    componentType,
    facingId,
    directionFilter = defaultDirectionFilter,
    _ancestors = [],
    availableValues
) => {
    const available = availableValues?.length ? availableValues : defaultDirectionFilter;

    if (!isDirectionFilterApplied(directionFilter, available)) {
        return true;
    }

    const filter = getEffectiveDirectionFilter(directionFilter, available);

    if (isHorizontalComponentType(componentType)) {
        return filter.includes(HORIZONTAL_DIRECTION_KEY);
    }

    if (!DIRECTIONAL_COMPONENT_TYPES.includes(componentType)) {
        return false;
    }

    const key = normalizeFacingDirectionKey(componentType, facingId);
    const effectiveKey = key ?? "NA";
    return filter.includes(effectiveKey);
};

/**
 * Change that adds the component's new position to the saved interior filter,
 * so a toggled component stays visible. An unset filter already shows both.
 * @param {string[]|undefined} interiorFilter - Saved interior filter
 * @param {"interior"|"exterior"} position - Position the component is moving to
 * @returns {Array} Zero or one batched change
 */
export const getRevealPositionChanges = (interiorFilter, position) =>
    Array.isArray(interiorFilter) && !interiorFilter.includes(position)
        ? [{ path: INTERIOR_FILTER_PATH, value: [...interiorFilter, position] }]
        : [];

const getSubcomponentsList = (subcomponents = [], matchTypes = []) => subcomponents
    ?.filter(({ componentType = "" }) => matchTypes.includes(componentType)) || [];

const getDoorWindowsList = (subcomponents = [], matchTypes = []) => subcomponents
    ?.filter(({ componentType = "" }) => componentType === "door")
    .reduce((subAcc, subCur) => {
        const doorWindowsList =
            subCur.subcomponents?.filter(({ componentType = "" }) => matchTypes.includes(componentType)) || [];

        return [...subAcc, ...doorWindowsList];
    }, []);

const sortFilteredComponents = (filteredComponents) =>
    filteredComponents.sort((a, b) => {
        const isSubcomponent = (componentType) => ["door", "floorHeader", "window"].includes(componentType);
        if (!isSubcomponent(a.componentType) && !isSubcomponent(b.componentType) && a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder;
        }
        if (a.componentLabel !== b.componentLabel && a.componentLabel != null && b.componentLabel !== null) {
            if (a.componentLabel?.toLowerCase() < b.componentLabel?.toLowerCase()) {
                return -1;
            }
            if (a.componentLabel?.toLowerCase() > b.componentLabel?.toLowerCase()) {
                return 1;
            }
            return 0;
        }
        return 0;
    });

// Return a list of components of a certain type from a summary list of components
export const filterComponentsByType = (componentsList = [], matchTypes = []) => {
    // Gather main components and any subcomponents whose parents are not selected for filtering
    // (all other subcomponents are rendered via the parent)
    const filteredComponents = componentsList.reduce((acc, cur) => {
        const { componentType = "", subcomponents = [] } = cur;
        
        // Display subcomponents individually even if parent component is not selected for filtering
        if (!matchTypes.includes(componentType)) {
            var subCompList = [];
            var doorWindowsList = [];

            // Search one level down for subcomponents
            if (matchTypes.includes("door") || matchTypes.includes("floorHeader") || matchTypes.includes("window")) {
                subCompList = getSubcomponentsList(subcomponents, matchTypes);
            }
            // Search two levels down for subcomponents if door is not selected for filtering
            if (matchTypes.includes("window") && !matchTypes.includes("door")) {
                doorWindowsList = getDoorWindowsList(subcomponents, matchTypes);
            }

            return [...acc, ...subCompList, ...doorWindowsList];
        } else {
            // Ensure door windows are displayed if the parent door is not selected for filtering
            var doorWindowsList = [];

            if (matchTypes.includes("window") && !matchTypes.includes("door")) {
                doorWindowsList = getDoorWindowsList(subcomponents, matchTypes);
            }

            // Display only the main component and doorWindows if necessary
            return [...acc, cur, ...doorWindowsList];
        }
    }, []);

    // Sort by name for subcomponents, otherwise display order first
    return sortFilteredComponents(filteredComponents);
};

const itemMatchesDirection = (item, directionFilter, ancestors = [], availableValues) => {
    const { componentType = "", data = {} } = item;
    const facingId = data?.facingDirection?.id;
    return matchesDirectionFilter(componentType, facingId, directionFilter, ancestors, availableValues);
};

const ancestorFromItem = (item) => ({
    componentType: item?.componentType,
    facingId: item?.data?.facingDirection?.id,
});

const getMatchingDirectionalSubs = (
    subcomponents = [],
    directionFilter = [],
    ancestors = [],
    availableValues,
    componentFilter = defaultComponentFilter
) =>
    (subcomponents || [])
        .filter(({ componentType = "" }) => DIRECTIONAL_COMPONENT_TYPES.includes(componentType))
        .filter(({ componentType = "" }) => componentFilter.includes(componentType))
        .filter((sub) => itemMatchesDirection(sub, directionFilter, ancestors, availableValues));

// Windows under type-excluded doors are already promoted by filterComponentsByType
const getMatchingWindowsFromNonMatchingDoors = (
    subcomponents = [],
    directionFilter = [],
    ancestors = [],
    availableValues,
    componentFilter = defaultComponentFilter
) => {
    if (!componentFilter.includes("door") || !componentFilter.includes("window")) {
        return [];
    }

    return (subcomponents || [])
        .filter(({ componentType = "" }) => componentType === "door")
        .filter((door) => !itemMatchesDirection(door, directionFilter, ancestors, availableValues))
        .flatMap((door) =>
            getMatchingDirectionalSubs(
                door.subcomponents,
                directionFilter,
                [ancestorFromItem(door), ...ancestors],
                availableValues,
                componentFilter
            ).filter(({ componentType }) => componentType === "window")
        );
};

// Filter components by stored facingDirection; promote matching children only when parent is hidden
export const filterComponentsByDirection = (
    componentsList = [],
    directionFilter = defaultDirectionFilter,
    availableValues,
    componentFilter = defaultComponentFilter
) => {
    const available = availableValues?.length ? availableValues : defaultDirectionFilter;

    if (!isDirectionFilterApplied(directionFilter, available)) {
        return componentsList;
    }

    const filter = getEffectiveDirectionFilter(directionFilter, available);
    const filteredComponents = componentsList.reduce((acc, cur) => {
        const { subcomponents = [] } = cur;
        const parentAncestors = [ancestorFromItem(cur)];

        if (itemMatchesDirection(cur, filter, [], available)) {
            // Parent matches — nested children are filtered in parent UI.
            // Promote matching windows under doors that themselves do not match.
            return [
                ...acc,
                cur,
                ...getMatchingWindowsFromNonMatchingDoors(subcomponents, filter, parentAncestors, available, componentFilter),
            ];
        }

        // Parent does not match — promote matching directional children and windows under non-matching doors
        return [
            ...acc,
            ...getMatchingDirectionalSubs(subcomponents, filter, parentAncestors, available, componentFilter),
            ...getMatchingWindowsFromNonMatchingDoors(subcomponents, filter, parentAncestors, available, componentFilter),
        ];
    }, []);

    return sortFilteredComponents(filteredComponents);
};

// Filter components by interior/exterior status
// interiorFilter is an array like ["exterior", "interior"] or just one of them
export const filterComponentsByInterior = (componentsList = [], interiorFilter = ["exterior", "interior"]) => {
    // If both are selected, no filtering needed
    if (interiorFilter.includes("exterior") && interiorFilter.includes("interior")) {
        return componentsList;
    }

    // If neither selected, return empty (edge case)
    if (!interiorFilter.includes("exterior") && !interiorFilter.includes("interior")) {
        return [];
    }

    const showExterior = interiorFilter.includes("exterior");
    const showInterior = interiorFilter.includes("interior");

    return componentsList.filter((component) => {
        const { data = {}, componentType } = component;
        const isInterior = component.isInterior === true || data?.isInterior === true;
        
        // Only wall, ceiling, and expFloor can be interior; their subcomponents inherit the parent's position
        const canBeInterior = ["wall", "ceiling", "expFloor", "door", "window", "floorHeader"].includes(componentType);
        
        if (!canBeInterior) {
            // Non-interior-capable components are always exterior
            return showExterior;
        }
        
        if (isInterior) {
            return showInterior;
        } else {
            return showExterior;
        }
    });
};

const isDirectionFilterActiveFor = ({ directionFilter, availableDirectionValues }) =>
    isDirectionFilterApplied(directionFilter, availableDirectionValues);

const isInteriorFilterActiveFor = ({ interiorFilter = defaultInteriorFilter }) =>
    interiorFilter.length !== defaultInteriorFilter.length;

/** True when the active filters need component `data` (facingDirection / isInterior) to evaluate. */
export const filtersNeedComponentData = (filters = {}) =>
    isDirectionFilterActiveFor(filters) || isInteriorFilterActiveFor(filters);

/** Apply the enclosure component type, direction and interior filters to a summary list of components. */
export const applyComponentFilters = (componentItems = [], filters = {}) => {
    const {
        componentFilter = defaultComponentFilter,
        directionFilter = defaultDirectionFilter,
        availableDirectionValues,
        interiorFilter = defaultInteriorFilter,
    } = filters;

    let filteredItems = componentItems;

    if (componentFilter.length !== defaultComponentFilter.length) {
        filteredItems = filterComponentsByType(filteredItems, componentFilter);
    }

    if (isDirectionFilterActiveFor(filters)) {
        filteredItems = filterComponentsByDirection(
            filteredItems,
            directionFilter,
            availableDirectionValues,
            componentFilter
        );
    }

    if (isInteriorFilterActiveFor(filters)) {
        filteredItems = filterComponentsByInterior(filteredItems, interiorFilter);
    }

    return filteredItems;
};
